import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { ProjectTaskService } from 'src/app/private/services/project-task.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-project-task-comment',
  templateUrl: './project-task-comment.component.html',
  styleUrls: ['./project-task-comment.component.scss'],
})
export class ProjectTaskCommentComponent implements OnInit, OnDestroy {
  @Input() taskId!: any;

  @Input() projectId!: any;

  private destroy$ = new Subject<void>();

  // ── Data ──────────────────────────────────────────────────────────────────
  comments: any[] = [];

  loading = true;

  // ── Compose area ──────────────────────────────────────────────────────────
  commentText = '';

  isInternal = false;

  submitting = false;

  // ── Edit state ────────────────────────────────────────────────────────────
  editingCommentId: any = null;

  editText = '';

  editIsInternal = false;

  editSubmitting = false;

  // ── Reply state ───────────────────────────────────────────────────────────
  replyingToId: any = null;

  replyText = '';

  replySubmitting = false;

  // ── Delete state ──────────────────────────────────────────────────────────
  deletingCommentId: any = null;

  // ── Current user ─────────────────────────────────────────────────────────
  currentUserId: any;

  currentUserName = '';

  constructor(
    private api: ProjectTaskService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    const user = this.localStorage.getUser();

    this.currentUserId = user.user_id;

    this.currentUserName = `${user.fname || ''} ${user.lname || ''}`.trim();

    this.loadComments();
  }

  ngOnDestroy() {
    this.destroy$.next();

    this.destroy$.complete();
  }

  // ── Load Comments ─────────────────────────────────────────────────────────

  loadComments() {
    this.loading = true;

    this.api
      .getAllTaskComments({
        task_id: this.taskId,

        project_id: this.projectId,

        client_id: this.localStorage.getClientId(),

        limit: 100,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.comments = res?.rows ?? [];

          this.loading = false;
        },

        error: () => {
          this.loading = false;

          this.toastr.error('Failed to load comments');
        },
      });
  }

  // ── Add Comment ───────────────────────────────────────────────────────────

  addComment() {
    if (!this.commentText?.trim()) return;

    this.submitting = true;

    const payload = {
      task_id: this.taskId,

      project_id: this.projectId,

      comment: this.commentText.trim(),

      is_internal: this.isInternal,

      commented_by: this.currentUserId,

      client_id: this.localStorage.getClientId(),
    };

    this.api
      .createTaskComment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.commentText = '';

          this.isInternal = false;

          this.submitting = false;

          this.toastr.success('Comment added');

          this.loadComments();
        },

        error: () => {
          this.submitting = false;

          this.toastr.error('Failed to add comment');
        },
      });
  }

  // ── Edit Comment ──────────────────────────────────────────────────────────

  startEdit(comment: any) {
    this.editingCommentId = comment.comment_id;

    this.editText = comment.comment;

    this.editIsInternal = comment.is_internal;

    this.replyingToId = null;
  }

  cancelEdit() {
    this.editingCommentId = null;

    this.editText = '';
  }

  submitEdit() {
    if (!this.editText?.trim()) return;

    this.editSubmitting = true;

    const payload = {
      where: {
        comment_id: this.editingCommentId,
      },

      comment: this.editText.trim(),

      is_internal: this.editIsInternal,

      updated_by: this.currentUserId,
    };

    this.api
      .updateTaskComment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.editingCommentId = null;

          this.editText = '';

          this.editSubmitting = false;

          this.toastr.success('Comment updated');

          this.loadComments();
        },

        error: () => {
          this.editSubmitting = false;

          this.toastr.error('Failed to update comment');
        },
      });
  }

  // ── Reply ─────────────────────────────────────────────────────────────────

  startReply(comment: any) {
    this.replyingToId = comment.comment_id;

    this.replyText = '';

    this.editingCommentId = null;
  }

  cancelReply() {
    this.replyingToId = null;

    this.replyText = '';
  }

  submitReply(parentId: any) {
    if (!this.replyText?.trim()) return;

    this.replySubmitting = true;

    const payload = {
      task_id: this.taskId,

      project_id: this.projectId,

      parent_comment_id: parentId,

      comment: this.replyText.trim(),

      is_internal: this.isInternal,

      commented_by: this.currentUserId,

      client_id: this.localStorage.getClientId(),
    };

    this.api
      .createTaskComment(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.replyingToId = null;

          this.replyText = '';

          this.replySubmitting = false;

          this.toastr.success('Reply added');

          this.loadComments();
        },

        error: () => {
          this.replySubmitting = false;

          this.toastr.error('Failed to add reply');
        },
      });
  }

  // ── Delete ────────────────────────────────────────────────────────────────

  confirmDelete(id: any) {
    this.deletingCommentId = id;
  }

  cancelDelete() {
    this.deletingCommentId = null;
  }

  deleteComment(id: any) {
    this.api
      .deleteTaskComment({
        where: {
          comment_id: id,
        },

        deleted_by: this.currentUserId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deletingCommentId = null;

          this.toastr.success('Comment deleted');

          this.loadComments();
        },

        error: () => {
          this.deletingCommentId = null;

          this.toastr.error('Failed to delete comment');
        },
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  isOwner(comment: any): boolean {
    return String(comment.commented_by) === String(this.currentUserId);
  }

  getInitials(comment: any): string {
    const u = comment.commented_user;

    if (!u) return '?';

    const f = (u.fname || '').charAt(0).toUpperCase();

    const l = (u.lname || '').charAt(0).toUpperCase();

    return f + l || '?';
  }

  getFullName(comment: any): string {
    const u = comment.commented_user;

    if (!u) return 'Unknown';

    return `${u.fname || ''} ${u.lname || ''}`.trim() || 'Unknown';
  }

  getEmail(comment: any): string {
    return comment.commented_user?.email || '';
  }

  // ── Root Comments ─────────────────────────────────────────────────────────

  get rootComments(): any[] {
    return this.comments.filter((c) => !c.parent_comment_id);
  }

  // ── Replies ───────────────────────────────────────────────────────────────

  getReplies(parentId: any): any[] {
    return this.comments.filter(
      (c) => String(c.parent_comment_id) === String(parentId),
    );
  }

  // ── Time Helpers ──────────────────────────────────────────────────────────

  relativeTime(dateStr: string): string {
    if (!dateStr) return '';

    const diff = Date.now() - new Date(dateStr).getTime();

    const mins = Math.floor(diff / 60000);

    if (mins < 1) return 'just now';

    if (mins < 60) return `${mins}m ago`;

    const hrs = Math.floor(mins / 60);

    if (hrs < 24) return `${hrs}h ago`;

    const days = Math.floor(hrs / 24);

    if (days < 7) return `${days}d ago`;

    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return '';

    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // ── Avatar Helpers ────────────────────────────────────────────────────────

  avatarColor(name: string): string {
    const colors = [
      '#6366f1',
      '#8b5cf6',
      '#ec4899',
      '#f59e0b',
      '#10b981',
      '#3b82f6',
      '#ef4444',
      '#14b8a6',
    ];

    let hash = 0;

    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  }

  currentUserInitials(): string {
    return this.currentUserName
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  }
}
